/** ruda-api ContractDisplayStatus / ContractStatusDisplay 와 동일한 표시 상태 */

export const CONTRACT_DISPLAY_STATUS = {
  NOT_CONTRACTED: 'NOT_CONTRACTED',
  SIGNING_REVIEW: 'SIGNING_REVIEW',
  PENDING_REVIEW: 'PENDING_REVIEW',
  RECONTRACT_REQUESTED: 'RECONTRACT_REQUESTED',
  CONTRACT_COMPLETED: 'CONTRACT_COMPLETED',
  /** 계약 문서가 없을 때만 노출. 문서가 있으면 실제 검토 상태를 유지한다 */
  EXTERNAL_CONTRACT: 'EXTERNAL_CONTRACT',
} as const;

export const CONTRACT_DISPLAY_STATUS_LABEL: Record<string, string> = {
  [CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED]: '계약전',
  [CONTRACT_DISPLAY_STATUS.SIGNING_REVIEW]: '서명 완료(검토 중)',
  [CONTRACT_DISPLAY_STATUS.PENDING_REVIEW]: '확인 전',
  [CONTRACT_DISPLAY_STATUS.RECONTRACT_REQUESTED]: '재계약 요청',
  [CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED]: '계약 완료',
  [CONTRACT_DISPLAY_STATUS.EXTERNAL_CONTRACT]: '외부 계약',
};

export const CONTRACT_DOCUMENT_STATUS = {
  DRAFT: 'DRAFT',
  FINALIZING: 'FINALIZING',
  FINALIZE_FAILED: 'FINALIZE_FAILED',
  PDF_CREATED: 'PDF_CREATED',
  CANCELED: 'CANCELED',
} as const;

const LEGACY_LABEL_TO_STATUS: Record<string, string> = {
  '계약 전': CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED,
  계약: CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED,
  '외부 계약': CONTRACT_DISPLAY_STATUS.EXTERNAL_CONTRACT,
};

const LABEL_TO_STATUS = Object.fromEntries(
  Object.entries(CONTRACT_DISPLAY_STATUS_LABEL).map(([key, label]) => [
    label,
    key,
  ]),
);

export const normalizeContractDisplayStatus = (
  rawStatus: unknown,
): string | null => {
  const status = String(rawStatus ?? '').trim();
  if (!status) return null;

  const upper = status.toUpperCase();
  if (CONTRACT_DISPLAY_STATUS_LABEL[upper]) return upper;
  if (LABEL_TO_STATUS[status]) return LABEL_TO_STATUS[status];
  if (LEGACY_LABEL_TO_STATUS[status]) return LEGACY_LABEL_TO_STATUS[status];

  if (
    upper === CONTRACT_DOCUMENT_STATUS.FINALIZING ||
    upper === CONTRACT_DOCUMENT_STATUS.FINALIZE_FAILED
  ) {
    return CONTRACT_DISPLAY_STATUS.SIGNING_REVIEW;
  }

  if (
    upper === CONTRACT_DOCUMENT_STATUS.DRAFT ||
    upper === CONTRACT_DOCUMENT_STATUS.CANCELED ||
    upper === 'CANCELLED'
  ) {
    return CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED;
  }

  if (upper === 'COMPLETED' || upper === 'CONTRACTED') {
    return CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED;
  }

  return null;
};

export const getContractDisplayStatusLabel = (rawStatus: unknown): string => {
  const status = String(rawStatus ?? '').trim();
  if (!status) {
    return CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED];
  }

  const normalized = normalizeContractDisplayStatus(status);
  if (normalized && CONTRACT_DISPLAY_STATUS_LABEL[normalized]) {
    return CONTRACT_DISPLAY_STATUS_LABEL[normalized];
  }

  return status;
};

export const isNotContractedDisplayStatus = (rawStatus: unknown): boolean => {
  const normalized = normalizeContractDisplayStatus(rawStatus);
  return (
    normalized === CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED || normalized === null
  );
};

export const isSigningReviewDisplayStatus = (
  rawStatus: unknown,
  _reviewStatus?: unknown,
  documentStatus?: unknown,
): boolean => {
  const documentKey = String(documentStatus ?? '').toUpperCase();
  if (
    documentKey === CONTRACT_DOCUMENT_STATUS.FINALIZING ||
    documentKey === CONTRACT_DOCUMENT_STATUS.FINALIZE_FAILED
  ) {
    return true;
  }

  const normalized = normalizeContractDisplayStatus(rawStatus);
  if (normalized === CONTRACT_DISPLAY_STATUS.SIGNING_REVIEW) return true;

  const loose = String(rawStatus ?? '').replace(/\s+/g, '');
  return loose.includes('검토') && loose.includes('중');
};

export const isPendingReviewDisplayStatus = (
  rawStatus: unknown,
  reviewStatus?: unknown,
): boolean => {
  const reviewKey = String(reviewStatus ?? '').toUpperCase();
  if (reviewKey === CONTRACT_DISPLAY_STATUS.PENDING_REVIEW) return true;

  return (
    normalizeContractDisplayStatus(rawStatus) ===
    CONTRACT_DISPLAY_STATUS.PENDING_REVIEW
  );
};

export const isRecontractRequestedDisplayStatus = (
  rawStatus: unknown,
  reviewStatus?: unknown,
): boolean => {
  const reviewKey = String(reviewStatus ?? '').toUpperCase();
  if (reviewKey === CONTRACT_DISPLAY_STATUS.RECONTRACT_REQUESTED) return true;

  return (
    normalizeContractDisplayStatus(rawStatus) ===
    CONTRACT_DISPLAY_STATUS.RECONTRACT_REQUESTED
  );
};

export const isContractCompletedDisplayStatus = (
  rawStatus: unknown,
  reviewStatus?: unknown,
): boolean => {
  const reviewKey = String(reviewStatus ?? '').toUpperCase();
  if (reviewKey === CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED) return true;

  return (
    normalizeContractDisplayStatus(rawStatus) ===
    CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED
  );
};

export const hasFinalizedContractDocument = (
  rawStatus: unknown,
  reviewStatus?: unknown,
  documentStatus?: unknown,
): boolean => {
  const documentKey = String(documentStatus ?? '').toUpperCase();
  if (
    documentKey === CONTRACT_DOCUMENT_STATUS.PDF_CREATED ||
    documentKey === 'COMPLETED'
  ) {
    return true;
  }

  if (
    isSigningReviewDisplayStatus(rawStatus, reviewStatus, documentStatus) ||
    isNotContractedDisplayStatus(rawStatus)
  ) {
    return false;
  }

  return (
    isPendingReviewDisplayStatus(rawStatus, reviewStatus) ||
    isRecontractRequestedDisplayStatus(rawStatus, reviewStatus) ||
    isContractCompletedDisplayStatus(rawStatus, reviewStatus)
  );
};

export const isContractDocumentFinalizedStatus = (
  documentStatus: unknown,
): boolean => {
  const status = String(documentStatus ?? '').toUpperCase();
  return (
    status === CONTRACT_DOCUMENT_STATUS.PDF_CREATED || status === 'COMPLETED'
  );
};

export const isContractDocumentSigningReviewStatus = (
  documentStatus: unknown,
): boolean => {
  const status = String(documentStatus ?? '').toUpperCase();
  return (
    status === CONTRACT_DOCUMENT_STATUS.FINALIZING ||
    status === CONTRACT_DOCUMENT_STATUS.FINALIZE_FAILED
  );
};
